import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useState } from 'react'

import { updatePartnerAdditional } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { IPartnerFormAdditional } from 'clickNgo/entities/partner/models/form/additional'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { resetAllModifiedDataByKey, useGetFormDataField } from 'cng-common/features/form'
import { TForm } from 'cng-common/features/form/models'
import Logger from 'core/utils/Logger'

import {
  PartnerEditorFormKeys,
  PartnerEditorFormSettings,
  UpdatePartnerResult,
} from '../../../../configs'
import { IPartnerEditorData } from '../../../../models'

interface IUseEditPartnerAdditionalInfoHandlerParams {
  partner: IPartner
}

export const useEditPartnerAdditionalInfoHandler = ({
  partner,
}: IUseEditPartnerAdditionalInfoHandlerParams) => {
  const dispatch = useAppDispatch()
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )

  const [currentMenuItem] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.CurrentMenuItem
  )

  const handleOnPartnerAdditionalChangesApply = useCallback(
    async ({ modifiedFields, formData, invalidFields }: TForm<IPartnerFormAdditional>) => {
      const hasInvalidModifiedFields = Object.keys(invalidFields).some((invalidField) =>
        modifiedFields.includes(invalidField)
      )

      if (hasInvalidModifiedFields) {
        return UpdatePartnerResult.Failed
      }

      const updatedPartnerData: IPartner = {
        ...partner,
        ...formData,
        modifiedValues: modifiedFields,
      }

      updateActualData({ ...actualData, partner: updatedPartnerData })

      return updatePartnerAdditional(updatedPartnerData)
        .then((result) => {
          if (result.status === 'success') {
            updateActualData({
              ...actualData,
              partner: { ...updatedPartnerData },
            })
            return UpdatePartnerResult.Updated
          } else {
            setErrorMessages(getJSendErrorMessages(result))
            return UpdatePartnerResult.Failed
          }
        })
        .catch((error: Error) => {
          Logger.error(error)
          setErrorMessages([error.message])
          return UpdatePartnerResult.Failed
        })
    },
    [actualData, partner, updateActualData]
  )

  const handleOnPartnerAdditionalChangesCancel = useCallback(
    ({ modifiedFields }: TForm<IPartnerFormAdditional>) => {
      if (modifiedFields.length) {
        dispatch(resetAllModifiedDataByKey(PartnerEditorFormKeys.AdditionalInfo))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    return () => {
      setErrorMessages([])
    }
  }, [currentMenuItem])

  return {
    handleOnPartnerAdditionalChangesApply,
    handleOnPartnerAdditionalChangesCancel,
    errorMessages,
  }
}
